<template>
  <div class="searchFilter">
    <div class="searchFilter__top">
      <div class="searchFilter__inputWrapper">
        <span class="icon icon-magnifier searchFilter__icon" />
        <input
          v-model="searchTerm"
          type="search"
          class="tb-input searchFilter__input"
          placeholder="Search"
          data-test="producer-searchFilter-search"
        />
        <button
          v-show="searchTerm"
          class="searchFilter__clearSearch"
          data-test="producer-searchFilter-clearSearch"
          @click="searchTerm = ''"
        >
          <span class="sr-only">Clear search term</span>
          <span class="fa fa-times"></span>
        </button>
      </div>
      <div
        v-if="!searchOnly"
        class="searchFilter__filterWrap"
      >
        <button
          class="searchFilter__openTags"
          ref="openButton"
          data-test="producer-searchFilter-openTags"
          aria-haspopup="listbox"
          :aria-expanded="isPopupOpen"
          :aria-controls="`listbox-${_uid}`"
          @click="isPopupOpen = !isPopupOpen"
        >
          <span class="fak fa-bars-filter-regular"></span>
          Filter by tags
        </button>

        <!-- multiselect popup -->
        <div
          v-if="isPopupOpen"
          class="searchFilter__panel"
          v-click-outside="handleClickOutside"
          data-test="producer-searchFilter-popup"
        >
          <div class="flex items-center margin-bottom-sm">
            <div class="tb-checkbox margin-bottom-none">
              <input
                v-model="allSelected"
                type="checkbox"
                id="tagSelectAll"
                data-test="producer-searchFilter-selectAll"
              />

              <label class="checkbox sr-only" for="tagSelectAll">Select all</label>
            </div>

            <div class="searchFilter__inputWrapper searchFilter__optionSearch">
              <span class="icon icon-magnifier searchFilter__icon" />
              <input
                v-model="filterTerm"
                type="search"
                class="tb-input searchFilter__input"
                placeholder="Search"
                data-test="producer-searchFilter-optionSearch"
              />
            </div>
          </div>
          <ul
            :id="`listbox-${_uid}`"
            class="searchFilter__optionList"
            role="listbox"
            aria-multiselectable="true"
            aria-label="Option list"
          >
            <li
              v-for="o in filteredOptions"
              :key="o"
              class="searchFilter__optionListItem"
              role="option"
              :aria-label="o"
              :aria-selected="selected.includes(o)"
              :data-test="`producer-searchFilter-option:${o}`"
            >
              <div class="tb-checkbox">
                  <input
                    :checked="selected.includes(o)"
                    type="checkbox"
                    :id="o"
                    :data-test="`producer-searchFilter-checkBox:${o}`"
                    @change="onOptionSelect(o)"
                  />

                  <label class="checkbox" :for="o">{{ o }}</label>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <div v-show="selectedOptions.length" class="searchFilter__tags">
      <span
        v-for="tag in selectedOptions"
        :key="tag"
        class="searchFilter__tag"
        :data-test="`producer-searchFilter-tag:${tag}`"
      >
        {{ tag }}
        <button
          class="searchFilter__tagClose"
          :data-test="`producer-searchFilter-tagClose:${tag}`"
          @click="selectedOptions = selectedOptions.filter((o) => o !== tag)"
        >
          <span class="fa fa-times searchFilter__tagCloseIcon" />
        </button>
      </span>
    </div>
  </div>
</template>

<script>
import { debounce } from 'lodash-es';

export default {
  name: 'SearchFilter',
  props: {
    value: {
      type: String,
      required: true,
    },
    options: {
      type: Array,
      default: () => [],
    },
    selected: {
      type: Array,
      default: () => [],
    },
    searchOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isPopupOpen: false,
      filterTerm: '',
    };
  },
  computed: {
    searchTerm: {
      get() {
        return this.value;
      },
      set: debounce(function(value) {
        this.$emit('input', value);
      }, 500),
    },

    filteredOptions() {
      if (!this.filterTerm) return this.options;

      return this.options.filter((o) => o.toLocaleLowerCase().includes(this.filterTerm.toLocaleLowerCase()));
    },

    allSelected: {
      get() {
        return this.selectedOptions.length === this.options.length;
      },
      set(value) {
        this.selectedOptions = value ? this.options : [];
      },
    },

    selectedOptions: {
      get() {
        return this.selected;
      },
      set(value) {
        this.$emit('update:selected', value);
      },
    },
  },
  methods: {
    onOptionSelect(option) {
      const value = this.selectedOptions.includes(option)
        ? this.selectedOptions.filter((o) => o !== option)
        : [...this.selectedOptions, option];

      this.selectedOptions = value;
    },
    handleClickOutside(e) {
      if (e.target === this.$refs.openButton) {
        return;
      }

      this.isPopupOpen = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.searchFilter {
  &__top {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  &__inputWrapper {
    position: relative;
    flex: 1;
  }

  &__icon {
    position: absolute;
    left: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    width: auto;
    height: auto;
    line-height: 1;
  }

  &__clearSearch {
    background: none;
    border: none;
    position: absolute;
    right: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    cursor: pointer;
  }

  &__input {
    padding-left: 3rem;
    padding-right: 2.5rem;
    border-radius: 30px;
  }

  &__filterWrap {
    position: relative;
    overflow: visible;
  }

  &__openTags {
    display: flex;
    align-items: center;
    gap: 1rem;
    background: none;
    background-color: variables.$color-12dp;
    border: none;
    padding: 6px 8px;
    border-radius: 30px;
    height: 32px; // match input height
  }

  &__panel {
    position: absolute;
    top: 100%;
    right: 0;
    z-index: 100;
    background-color: variables.$color-02dp;
    padding: 8px;
    min-width: 100%;
    max-height: 400px;
    display: flex;
    flex-direction: column;
  }

  &__optionList {
    list-style: none;
    flex: 1;
    padding: 0;
    margin: 0;
    overflow-y: auto;
  }

  &__optionListItem {
    & + & {
      margin-top: 4px;
    }
  }

  &__optionSearch {
    margin-left: auto;
    min-width: 150px;
  }

  &__tags {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-top: 1rem;
  }

  &__tag {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    background-color: variables.$color-12dp;
    padding: 4px 8px;
    border-radius: 30px;
    font-size: 12px;
  }

  &__tagClose {
    background: none;
    border: 1px solid variables.$color-on-surface-medium;
    border-radius: 50%;
    padding: 2px;
    line-height: 1;
    font-size: 10px;
    cursor: pointer;
  }

  &__tagCloseIcon {
    vertical-align: middle;
    text-align: center;
    width: 9px;
    height: 9px;
    font-size: 9px;
  }
}
</style>
